import { EAuthenticationType, ESdChangeReason, IDatasourceDto } from "@kuika/kuika-cl-model";
import _ from "lodash";
import ObjectHash from "object-hash";
import { FormInstance } from "rc-field-form";
import * as React from "react";
import { connect } from "react-redux";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { InputTooltip } from "../../common/input-tooltip";
import KuikaFormPage, {
  KuikaFormAsideBlock,
  KuikaFormField,
  KuikaFormSection
} from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaTabs, { KuikaTabOption } from "../../ui-kit/kuika-tabs/kuika-tabs";
import { ensureTrailingDraftRow, withoutBlankRows } from "../shared/kuika-edit-grid/draft-rows";
import "./create-connection-form.scss";
import "./editor-bar-surface.scss";
import { ConnectionRenameTitle } from "./connection-rename-title";
import { ConnectionSwitcherChip } from "./editor-switcher/connection-switcher-chip";
import { RestConnectionAuthSection } from "./rest-api/rest-connection-auth-section";
import {
  blankHeaderParameter,
  buildHeaderRecord,
  headerParametersForAuthType,
  IApiKeyParameter,
  IBasicAuthCredentials,
  IOrganizedHeaderParameters,
  IParsedHeaderParameters,
  isBlankHeaderParameter,
  organizeHeaderParameters,
  parseHeaderRecord
} from "./rest-api/rest-connection-headers";
import {
  buildAuthTypeTabOptions,
  IRestConnectionField,
  IRestConnectionRequest,
  IRestConnectionSpec,
  REST_AUTH_TYPES,
  shouldOpenEndpointEditorAfterSave
} from "./rest-connection-specs";

export interface IRestConnectionFormState {
  isSaveDisabled: boolean;
  isNameOnEdit: boolean;
  datasourceName: string;
  authType: EAuthenticationType;
  /** Which row already holds each generated header; -1 when none does. */
  userNameHeaderParameterIndex: number;
  authTokenHeaderParameterIndex: number;
  basicAuthHeaderParameterIndex: number;
}

export interface IRestConnectionFormProps {
  spec: IRestConnectionSpec;
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (showModal: boolean) => void;
}

const emptyRequest = (): IRestConnectionRequest => ({
  prefix: "",
  serviceBaseUrl: "",
  clientIdKeyName: "",
  clientId: "",
  clientSecretKeyName: "",
  clientSecret: "",
  currentUserEmailName: "",
  authType: EAuthenticationType.NoAuth,
  extraHeaderParameters: {}
});

class RestConnectionForm extends PureSdComponent<IRestConnectionFormProps & IWithSDProps, IRestConnectionFormState> {
  formRef = React.createRef<FormInstance>();

  objectHash: string = "";

  constructor(props: IRestConnectionFormProps & IWithSDProps) {
    super(props);
    this.state = {
      isSaveDisabled: false,
      isNameOnEdit: false,
      datasourceName: "",
      authType: EAuthenticationType.NoAuth,
      userNameHeaderParameterIndex: -1,
      authTokenHeaderParameterIndex: -1,
      basicAuthHeaderParameterIndex: -1
    };
  }

  componentDidMount() {
    this.handleLoadDatasource();
  }

  componentDidUpdate(prevProps: IRestConnectionFormProps & IWithSDProps) {
    if (
      this.props.editingDatasource?.id !== prevProps.editingDatasource?.id ||
      this.props.sd.activeCreatingDatasourceName !== prevProps.sd.activeCreatingDatasourceName
    ) {
      this.handleLoadDatasource();
    }
    // A save in flight answers the pending navigation itself (saveDatasource).
    if (this.props.nextCallback && !this.state.isSaveDisabled) {
      this.handleCloseDatasource();
    }
  }

  /** `<spec prefix><suffix>`, the shape the E2E page objects already read. */
  private testId = (suffix: string): string => `${this.props.spec.testIdPrefix}${suffix}`;

  handleLoadDatasource = (): void => {
    const { spec, editingDatasource } = this.props;
    const creatingName: string | undefined = this.props.sd?.activeCreatingDatasourceName;
    const stored: IRestConnectionRequest | undefined = editingDatasource
      ? spec.getRequest(editingDatasource)
      : undefined;
    const request: IRestConnectionRequest = stored ?? emptyRequest();

    const fieldsValue: Record<string, unknown> = {
      name: editingDatasource?.name ?? creatingName ?? "",
      ClientIdKeyName: request.clientIdKeyName ?? "",
      ClientId: request.clientId ?? "",
      ClientSecretKeyName: request.clientSecretKeyName ?? "",
      ClientSecret: request.clientSecret ?? "",
      CurrentUserEmailName: request.currentUserEmailName ?? "",
      BasicAuthUsername: "",
      BasicAuthPassword: "",
      description: request.description ?? ""
    };
    spec.fields.forEach((field: IRestConnectionField): void => {
      fieldsValue[field.name] = field.read(request);
    });

    const authType: EAuthenticationType = stored ? request.authType : EAuthenticationType.NoAuth;
    let storedRows: IApiKeyParameter[] = [];

    if (stored) {
      const parsed: IParsedHeaderParameters = parseHeaderRecord(request.extraHeaderParameters, authType);
      storedRows = parsed.parameters;
      fieldsValue.BasicAuthUsername = parsed.basicAuth.username;
      fieldsValue.BasicAuthPassword = parsed.basicAuth.password;
    }
    // The stored headers and the blank row the next one is typed into.
    const headerRows: IApiKeyParameter[] = ensureTrailingDraftRow(
      storedRows,
      isBlankHeaderParameter,
      blankHeaderParameter
    );
    fieldsValue.extraHeaderParameters = headerRows;

    this.setState({ authType, datasourceName: String(fieldsValue.name), isNameOnEdit: false }, () => {
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.objectHash = this.getObjectHash(authType);
      this.handleCheckAndOrganizeExtraHeaderParameters(headerRows);
    });
  };

  /**
   * What "unsaved" means for this form. The name is part of it: the OData copy left it out,
   * so renaming a datasource and closing lost the rename with no prompt.
   */
  getObjectHash(authType?: EAuthenticationType): string {
    // The rename field is left out: `datasourceName` already carries the name, and the field
    // itself only exists while a rename is open — `getFieldsValue()` reports registered
    // fields only, so including it made an untouched collection look edited.
    const { name: _renameField, ...values } = this.formRef.current?.getFieldsValue() ?? {};
    // The header table's draft row is where the next header goes, not an edit — and it can arrive
    // after the baseline is taken — so blank rows are left out of both sides of the compare.
    return ObjectHash({
      authType: authType ?? this.state.authType,
      datasourceName: this.state.datasourceName,
      values: {
        ...values,
        extraHeaderParameters: withoutBlankRows(values.extraHeaderParameters, isBlankHeaderParameter)
      }
    });
  }

  closeDataSource(): void {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource(): void {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  handleSetExtraHeaderParameters = (extraHeaderParameters: IApiKeyParameter[]): void => {
    const formValues = { ...this.formRef.current?.getFieldsValue() };
    formValues.extraHeaderParameters = extraHeaderParameters;
    this.formRef.current?.setFieldsValue({ ...formValues });
  };

  handleCheckAndOrganizeExtraHeaderParameters = (extraHeaderParameters: IApiKeyParameter[] | undefined): void => {
    const rows: IApiKeyParameter[] = extraHeaderParameters ?? [];
    const organized: IOrganizedHeaderParameters = organizeHeaderParameters(rows);

    this.setState({
      userNameHeaderParameterIndex: organized.userNameHeaderParameterIndex,
      authTokenHeaderParameterIndex: organized.authTokenHeaderParameterIndex,
      basicAuthHeaderParameterIndex: organized.basicAuthHeaderParameterIndex
    });
    // Written back at once, and only when organizing changed a row. This runs on every keystroke
    // in the table; deferred to the state callback it wrote back rows computed a moment earlier —
    // a list that no longer had the draft row the same keystroke had just appended.
    if (!_.isEqual(rows, organized.parameters)) {
      this.handleSetExtraHeaderParameters(organized.parameters);
    }
  };

  handleSelectAuthType = (authType: EAuthenticationType): void => {
    this.setState(
      {
        authType,
        userNameHeaderParameterIndex: -1,
        authTokenHeaderParameterIndex: -1,
        basicAuthHeaderParameterIndex: -1
      },
      () => {
        const rows: IApiKeyParameter[] = headerParametersForAuthType(
          authType,
          this.formRef.current?.getFieldValue("extraHeaderParameters")
        );
        this.handleSetExtraHeaderParameters(rows);
        this.handleCheckAndOrganizeExtraHeaderParameters(rows);
      }
    );
  };

  handleSubmitNameChange = (): void => {
    const errors: string[] | undefined = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) return;

    this.setState({ isNameOnEdit: false, datasourceName: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = (): void => {
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a valid Datasource name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.datasourceName
    });
    this.setState({ isNameOnEdit: false });
  };

  saveDatasource = async (): Promise<void> => {
    const { spec, editingDatasource } = this.props;

    // The OData copy had no such guard, so a datasource could be saved mid-rename and keep
    // its old name while the heading showed the new one.
    if (this.state.isNameOnEdit) {
      KMainFunctions.displayWarningNotification(
        `You should apply your Datasource Name before save the ${spec.copyName} Datasource`
      );
      return;
    }

    this.setState({ isSaveDisabled: true });
    const form = this.formRef.current!.getFieldsValue();
    const name: string = this.state.datasourceName;

    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === name?.toLowerCase()
    );
    const isEqualToWfName = this.props.sd?.workFlowList?.find((wf) => wf.name.toLowerCase() === name?.toLowerCase());
    // Compared by id as well, so editing a datasource does not collide with itself — the
    // copies used `&& !editingDatasource`, which let a rename onto a taken name through.
    const isEqualToDatasourceName = this.props.sd?.datasources?.some(
      (datasource) => datasource.name.toLowerCase() === name?.toLowerCase() && datasource.id !== editingDatasource?.id
    );

    if (isEqualToDatasourceName) {
      this.setState({ isSaveDisabled: false });
      KMainFunctions.displayWarningNotification("Datasource name already exist.");
      return;
    }
    if (isEqualToScreenName || isEqualToWfName) {
      this.setState({ isSaveDisabled: false });
      KMainFunctions.displayWarningNotification("Datasource name can not be equal to screen name or Process name.");
      return;
    }

    const credentials: IBasicAuthCredentials = {
      username: form.BasicAuthUsername,
      password: form.BasicAuthPassword
    };
    const previous: IRestConnectionRequest | undefined = editingDatasource
      ? spec.getRequest(editingDatasource)
      : undefined;

    const request: IRestConnectionRequest = {
      ...emptyRequest(),
      prefix: name,
      clientIdKeyName: form.ClientIdKeyName,
      clientId: form.ClientId,
      clientSecretKeyName: form.ClientSecretKeyName,
      clientSecret: form.ClientSecret,
      // Loaded by the copies and then never written back, so saving an edited datasource
      // dropped it.
      currentUserEmailName: form.CurrentUserEmailName,
      authType: this.state.authType,
      extraHeaderParameters: buildHeaderRecord(form.extraHeaderParameters, credentials),
      description: form.description,
      // The endpoints and their schemas belong to the collection, not to this sheet.
      paths: previous?.paths,
      schemas: previous?.schemas
    };
    spec.fields.forEach((field: IRestConnectionField): void => {
      field.write(request, form[field.name]);
    });

    const ds = {} as IDatasourceDto;
    if (editingDatasource?.id) ds.id = editingDatasource.id;
    ds.datasourceType = spec.datasourceType;
    ds.name = name;
    spec.setRequest(ds, request);

    try {
      const response = await DatasourceService.SaveDatasource(ds);
      if (response.status !== 200) return;

      this.props.sd.refreshActions();
      await this.props.sd.refreshDatabases();
      this.props.setShowModal(false);

      // Handed the datasource this form built, not the response body: on a create the saved
      // row's id is not known here, and the endpoint editor resolves it by name and type.
      if (shouldOpenEndpointEditorAfterSave(spec, Boolean(editingDatasource?.id), Boolean(this.props.nextCallback))) {
        this.props.sd.setActiveEditingDatasource(ds, -1);
        return;
      }
      this.closeDataSource();
    } catch (error) {
      KMainFunctions.warningHandler(error);
    } finally {
      this.setState({ isSaveDisabled: false });
    }
  };

  private renderField = (field: IRestConnectionField): React.ReactNode => (
    <KuikaFormField
      key={field.name}
      name={field.name}
      label={field.label}
      className="kuika-form-section__wide"
      labelSuffix={<InputTooltip description={field.description} helpLink={this.props.spec.helpLink} />}
      rules={[{ required: true, message: field.requiredMessage }]}
    >
      <KuikaInput size="md" placeholder={field.placeholder} data-testid={`${this.testId(field.name)}Input`} />
    </KuikaFormField>
  );

  private renderName = (title: string): React.ReactNode => (
    <ConnectionRenameTitle
      title={title}
      identity={
        <ConnectionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          current={this.props.editingDatasource}
          name={this.state.datasourceName}
          datasourceType={this.props.spec.datasourceType}
          triggerTestId="restConnectionSwitcherTrigger"
          onManageAll={() => this.handleCloseDatasource()}
        />
      }
      isEditing={this.state.isNameOnEdit}
      canDiscard={Boolean(this.state.datasourceName)}
      onBeginEdit={() => this.setState({ isNameOnEdit: true })}
      onSubmit={this.handleSubmitNameChange}
      onDiscard={this.handleAbortNameChange}
      placeholder="Collection Name"
      rules={[
        { required: true, message: "Please enter a Collection name" },
        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Collection Name is invalid" }
      ]}
      renameTestId={this.testId("DatasourceNameEditButton")}
      inputTestId={this.testId("DatasourceNameInput")}
      submitTestId={this.testId("DatasourceNameSubmitButton")}
    />
  );

  // A field, not a summary, so it keeps its own label and the block takes no caption.
  private renderDescription(): React.ReactNode {
    return (
      <KuikaFormAsideBlock>
        <KuikaFormField name="description" label="Description">
          <KuikaInput
            size="md"
            type="textarea"
            rows={5}
            placeholder={this.props.spec.descriptionPlaceholder}
            data-testid={this.testId("DescriptionInput")}
          />
        </KuikaFormField>
      </KuikaFormAsideBlock>
    );
  }

  render(): React.ReactNode {
    const { spec } = this.props;
    const authOptions: KuikaTabOption[] = buildAuthTypeTabOptions(REST_AUTH_TYPES, spec.testIdPrefix);

    return (
      <>
        <KuikaFormPage
          className="kuika-connection-form"
          title={this.state.datasourceName}
          formName="dsModal"
          formRef={this.formRef}
          onFinish={this.saveDatasource}
          // Read from the values handed over, not through `formRef`: the header table's draft row
          // is added as the page mounts, before the form's ref is attached.
          onValuesChange={(changedValues, values) => {
            if (!changedValues.extraHeaderParameters) return;
            this.handleCheckAndOrganizeExtraHeaderParameters(values.extraHeaderParameters);
          }}
          isLoading={this.state.isSaveDisabled}
          renderTitle={this.renderName}
          submitLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
          submitTestId={this.testId("ConnectionSubmitButton")}
          cancelTestId={this.testId("ConnectionCancelButton")}
          onCancel={() => this.handleCloseDatasource()}
          aside={this.renderDescription()}
          learnMore={{
            label: `${spec.copyName} user guide`,
            href: spec.helpLink,
            testId: this.testId("LearnMoreLink")
          }}
          afterCreate={this.props.editingDatasource ? undefined : spec.afterCreate}
        >
          <KuikaFormSection legend="Endpoint" hint={spec.endpointHint} testId={this.testId("EndpointSection")}>
            {spec.fields.map(this.renderField)}
          </KuikaFormSection>

          <KuikaFormSection
            legend="Authentication"
            hint={spec.authenticationHint}
            columns={2}
            testId={this.testId("CredentialsSection")}
          >
            {/* Deliberately not a `KuikaFormField`: the auth type decides which rows the
                header table carries, so the form reacts to it as state and persists it from
                there. Bound to the store it would be handed back a `value` and an `onChange`
                that know nothing about that. The markup matches a field's so it still reads
                as one — but a caption, not a `<label>`: a segmented control has no single
                control to point one at, so the group is named by `ariaLabel` instead.
                Segments rather than a select: four short choices that each reshape the
                section below read better all at once. `lg` is the 32px rung the inputs use. */}
            <div className="kuika-form-field kuika-form-section__wide">
              <span className="kuika-form-field__label">
                Authentication type
                <InputTooltip
                  description="You can choose the authentication method to use to access the API."
                  helpLink={spec.helpLink}
                />
              </span>
              <KuikaTabs
                size="lg"
                ariaLabel="Authentication type"
                dataTestId={this.testId("AuthTypeSelect")}
                options={authOptions}
                value={String(this.state.authType)}
                onChange={(value: string): void => this.handleSelectAuthType(Number(value) as EAuthenticationType)}
              />
            </div>
            <RestConnectionAuthSection
              authType={this.state.authType}
              userNameHeaderParameterIndex={this.state.userNameHeaderParameterIndex}
              authTokenHeaderParameterIndex={this.state.authTokenHeaderParameterIndex}
              basicAuthHeaderParameterIndex={this.state.basicAuthHeaderParameterIndex}
              helpLink={spec.helpLink}
              testIdPrefix={spec.testIdPrefix}
            />
          </KuikaFormSection>
        </KuikaFormPage>
        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={spec.closeType}
          triggerModal={this.props.showModal}
          isLoading={this.state.isSaveDisabled}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => false;
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback,
  showModal: state.unsavedChangesModal.showModal
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const ConnectedRestConnectionForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<IRestConnectionFormProps>(RestConnectionForm));

export interface IRestConnectionFormOwnProps {
  editingDatasource?: IDatasourceDto;
}

/**
 * Binds one spec to the shared form, so each `create-connection-*` file stays the mount
 * point its callers already import and nothing else.
 */
export const createRestConnectionForm = (
  spec: IRestConnectionSpec
): React.FunctionComponent<IRestConnectionFormOwnProps> => {
  const SpecForm = (props: IRestConnectionFormOwnProps): React.ReactElement => (
    <ConnectedRestConnectionForm spec={spec} {...props} />
  );
  SpecForm.displayName = `RestConnectionForm(${spec.copyName})`;
  return SpecForm;
};
